import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { toast } from 'sonner';
import { Eye, EyeOff, Shield, Smartphone, AlertTriangle, ArrowLeft, Check } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ================================================================
   LoginPage — Flux 2FA complet
   Étape 1 : email + mot de passe
   Étape 2 : code OTP (si 2FA activé pour ce compte)
   ================================================================ */
export default function LoginPage() {
  // Étape du flux : 'credentials' | 'otp' | 'success'
  const [step, setStep] = useState('credentials');
  const [mode, setMode] = useState('login');       // 'login' | 'register'

  // Formulaire credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [profileType, setProfileType] = useState('particulier');
  const [showPwd, setShowPwd] = useState(false);

  // 2FA
  const [preAuthToken, setPreAuthToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpAttempts, setOtpAttempts] = useState(0);

  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  /* ── ÉTAPE 1 : Connexion email + mot de passe ── */
  async function handleCredentials(e) {
    e.preventDefault();
    setLoading(true);

    // Helper robuste : lit le body JSON sans risquer "Body is disturbed or locked"
    async function safeJson(response) {
      try {
        const text = await response.text();
        return text ? JSON.parse(text) : {};
      } catch {
        return {};
      }
    }

    try {
      if (mode === 'register') {
        // Inscription — récupérer le code parrainage stocké
        const refCode = localStorage.getItem('ose_ref_code') || '';
        const res = await fetch(`${BACKEND}/api/auth/register`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password, nom, prenom,
            profile: profileType,
            ...(refCode ? { ref_code: refCode } : {})
          })
        });
        const data = await safeJson(res);
        if (!res.ok) throw new Error(data.detail || 'Erreur inscription');
        if (refCode) localStorage.removeItem('ose_ref_code');
        login(data.access_token, data.user);
        toast.success('Compte créé !');
        window.location.href = data.user?.role === 'admin' ? '/admin' : data.user?.role === 'partenaire' ? '/partenaire' : '/';
        return;
      }

      // Connexion via le nouvel endpoint 2FA
      const res = await fetch(`${BACKEND}/api/auth/login-2fa`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data.detail || (res.status === 429 ? 'Trop de tentatives, réessayez dans 1 minute.' : 'Email ou mot de passe incorrect');
        throw new Error(msg);
      }

      if (data.otp_required) {
        // 2FA actif → passer à l'étape OTP
        setPreAuthToken(data.pre_auth_token);
        setStep('otp');
        toast('Code OTP requis', {
          description: 'Consultez Google Authenticator ou Authy.',
          icon: <Smartphone size={16} />
        });
      } else {
        // Pas de 2FA → login direct
        login(data.access_token, data.user);
        toast.success('Connexion réussie !');
        const dest = data.user?.role === 'admin' ? '/admin' : data.user?.role === 'partenaire' ? '/partenaire' : '/';
        window.location.href = dest;
      }
    } catch (err) {
      const msg = err.message || '';
      // Masquer les erreurs techniques cryptiques, remplacer par un message clair
      if (msg.includes('disturbed') || msg.includes('locked') || msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        toast.error('Problème de connexion. Vérifiez votre connexion internet et réessayez.');
      } else {
        toast.error(msg || 'Identifiants incorrects');
      }
    } finally {
      setLoading(false);
    }
  }

  /* ── ÉTAPE 2 : Vérification OTP ── */
  async function handleOtp(e) {
    e.preventDefault();
    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      toast.error('Le code OTP doit contenir exactement 6 chiffres.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/login-2fa/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pre_auth_token: preAuthToken, otp_code: otpCode })
      });
      const data = await res.json();

      if (!res.ok) {
        const attempts = otpAttempts + 1;
        setOtpAttempts(attempts);
        setOtpCode('');

        if (attempts >= 3) {
          // Trop de tentatives → retour au login
          toast.error('Trop de tentatives. Recommencez depuis le début.', { duration: 5000 });
          setStep('credentials');
          setPreAuthToken('');
          setOtpAttempts(0);
        } else {
          toast.error(data.detail || 'Code OTP incorrect. Réessayez.');
        }
        return;
      }

      // OTP valide → session complète
      login(data.access_token, data.user);
      setStep('success');
      toast.success('Authentification complète ✓');
      // Redirect via window.location for clean auth state
      window.location.href = data.user?.role === 'admin' ? '/admin' : '/';
    } catch (err) {
      toast.error('Erreur de connexion. Réessayez.');
    } finally {
      setLoading(false);
    }
  }

  /* ── Retour à l'étape credentials ── */
  function goBackToCredentials() {
    setStep('credentials');
    setPreAuthToken('');
    setOtpCode('');
    setOtpAttempts(0);
  }

  /* ── Première connexion / mot de passe oublié : envoie le lien par email ── */
  async function handleForgotPassword() {
    if (!email) {
      toast.error('Saisissez votre email ci-dessus, puis réessayez.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND}/api/auth/request-set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      await res.json().catch(() => ({}));
      toast.success(`Si un compte existe, un lien vient d'être envoyé à ${email}.`);
    } catch {
      toast.error('Problème d\'envoi. Réessayez dans un instant.');
    }
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="flex items-center justify-center" style={{ minHeight: '80vh', padding: '2rem' }}>
        <div className="w-full max-w-md">

          {/* ============================================================
              SUCCESS — bref écran de confirmation
              ============================================================ */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'var(--teal-mist)' }}>
                <Check size={28} style={{ color: 'var(--teal)' }} />
              </div>
              <p className="font-display font-bold text-xl" style={{ color: 'var(--ink)' }}>
                Connexion validée
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Redirection en cours…</p>
            </div>
          )}

          {/* ============================================================
              ÉTAPE 1 — Email + mot de passe
              ============================================================ */}
          {step === 'credentials' && (
            <div>
              {/* En-tête */}
              <div className="text-center mb-8">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'var(--teal)', boxShadow: '0 8px 24px rgba(93,188,191,0.25)' }}>
                  <Shield size={22} color="#0B1E20" />
                </div>
                <h1 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>
                  {mode === 'login' ? 'Connexion' : 'Créer un compte'}
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  Show Événement — Espace sécurisé
                </p>
              </div>

              <div className="p-6 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(26,26,46,0.06)' }}>
                {/* Tabs */}
                <div className="flex rounded-xl mb-6 p-1" style={{ background: 'var(--bg)' }}>
                  <button onClick={() => setMode('login')}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                    style={{ background: mode === 'login' ? 'var(--teal)' : 'transparent', color: mode === 'login' ? '#0B1E20' : 'var(--text-muted)' }}>
                    Connexion
                  </button>
                  <button onClick={() => setMode('register')}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                    style={{ background: mode === 'register' ? 'var(--teal)' : 'transparent', color: mode === 'register' ? '#0B1E20' : 'var(--text-muted)' }}>
                    Inscription
                  </button>
                </div>

                <form onSubmit={handleCredentials} className="space-y-4">
                  {mode === 'register' && (
                    <>
                      {/* Choix du profil */}
                      <div>
                        <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--ink)' }}>
                          Vous êtes... *
                        </label>
                        <div className="flex flex-col gap-2">
                          {[
                            {
                              id: 'particulier',
                              icon: '👤',
                              label: 'Particulier',
                              color: '#5DBCBF',
                              avantages: [
                                '🎁 Livraison offerte jusqu\'à 20km',
                                '⭐ Programme fidélité + cashback 5%',
                                '📋 Devis en ligne instantané',
                                '🪑 Plan de table interactif offert',
                              ],
                            },
                            {
                              id: 'association',
                              icon: '🤝',
                              label: 'Association',
                              color: '#7DB1FF',
                              badge: '-15%',
                              avantages: [
                                '💰 -15% automatique sur toutes les locations',
                                '🎠 Gonflables à 100€ TTC (au lieu de 120€)',
                                '🚚 Livraison + installation offertes sur gonflables',
                                '💳 Paiement à la livraison possible',
                                '📄 Factures pour votre comptabilité asso',
                              ],
                            },
                            {
                              id: 'entreprise',
                              icon: '🏢',
                              label: 'Entreprise',
                              color: '#5DBCBF',
                              badge: '-15%',
                              avantages: [
                                '💰 -15% automatique sur toutes les locations',
                                '📑 Devis & factures avec mentions légales + TVA',
                                '💳 Paiement à la livraison possible',
                                '🎟️ Codes CE pour vos salariés',
                                '🏆 Espace Pro dédié dans votre compte',
                              ],
                            },
                            {
                              id: 'ce',
                              icon: '🎫',
                              label: 'CE / CSE',
                              color: '#C58B19',
                              badge: '-10%',
                              avantages: [
                                '💰 -10% automatique sur toutes les locations',
                                '💳 Paiement à la livraison possible',
                                '🎁 Cagnotte 10% reversée au CE sur chaque commande bénéficiaire',
                                '👥 Partage du lien CE à vos employés',
                                '📊 Suivi des bénéficiaires dans votre espace',
                              ],
                            },
                          ].map(p => {
                            const selected = profileType === p.id;
                            return (
                              <button key={p.id} type="button" onClick={() => setProfileType(p.id)}
                                className="text-left w-full rounded-xl overflow-hidden transition-all"
                                style={{
                                  border: selected ? `2px solid ${p.color}` : '1.5px solid var(--border)',
                                  background: selected ? `${p.color}08` : '#FAFAFA',
                                }}>
                                {/* Header */}
                                <div className="flex items-center justify-between px-3.5 py-2.5"
                                  style={{ background: selected ? `${p.color}12` : 'transparent' }}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{p.icon}</span>
                                    <span className="font-bold text-sm" style={{ color: selected ? p.color : 'var(--ink)' }}>
                                      {p.label}
                                    </span>
                                    {p.badge && (
                                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                        style={{ background: p.color, color: '#fff' }}>
                                        {p.badge}
                                      </span>
                                    )}
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0`}
                                    style={{
                                      borderColor: selected ? p.color : 'var(--border)',
                                      background: selected ? p.color : 'transparent'
                                    }}>
                                    {selected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                                  </div>
                                </div>
                                {/* Avantages — toujours visibles */}
                                <div className="px-3.5 pb-3 pt-1.5 space-y-1">
                                  {p.avantages.map((av, i) => (
                                    <p key={i} className="text-xs flex items-start gap-1.5" style={{ color: selected ? 'var(--ink)' : 'var(--text-muted)' }}>
                                      <span style={{ flexShrink: 0 }}>{av.slice(0, 2)}</span>
                                      <span>{av.slice(2)}</span>
                                    </p>
                                  ))}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Prénom + Nom */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Prénom</label>
                          <input type="text" value={prenom} onChange={e => setPrenom(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-xl text-sm" placeholder="Jean"
                            style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Nom</label>
                          <input type="text" value={nom} onChange={e => setNom(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-xl text-sm" placeholder="Dupont"
                            style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Email *</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      autoComplete="email" data-testid="login-email-input"
                      className="w-full px-3 py-2.5 rounded-xl text-sm" placeholder="vous@exemple.fr"
                      style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Mot de passe *</label>
                    <div className="relative">
                      <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'} data-testid="login-password-input"
                        className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      <button type="button" onClick={() => setShowPwd(!showPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={loading} data-testid="login-submit-btn"
                    className="w-full btn-teal py-3 rounded-xl text-sm font-bold disabled:opacity-50 mt-2">
                    {loading ? '…' : mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                  </button>
                </form>

                {/* Info sécurité */}
                {mode === 'login' && (
                  <>
                    <button type="button" onClick={handleForgotPassword} data-testid="login-forgot-btn"
                      className="w-full text-xs mt-4 font-semibold underline"
                      style={{ color: 'var(--teal)' }}>
                      Première connexion / mot de passe oublié ?
                    </button>
                    <div className="flex items-center gap-2 mt-3 p-3 rounded-xl"
                      style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                      <Shield size={13} style={{ color: 'var(--teal)', flexShrink: 0 }} />
                      <p className="text-xs" style={{ color: '#0B4B4E' }}>
                        Si la double authentification est activée, un code OTP vous sera demandé.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ============================================================
              ÉTAPE 2 — Code OTP
              ============================================================ */}
          {step === 'otp' && (
            <div>
              {/* En-tête */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(93,188,191,0.12)', border: '2px solid var(--teal)' }}>
                  <Smartphone size={28} style={{ color: 'var(--teal)' }} />
                </div>
                <h1 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>
                  Vérification OTP
                </h1>
                <p className="text-sm mt-2 max-w-xs mx-auto" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  Ouvrez <strong>Google Authenticator</strong> ou <strong>Authy</strong> et saisissez le code à 6 chiffres.
                </p>
              </div>

              <div className="p-6 rounded-2xl" style={{ background: '#FFFFFF', border: '1.5px solid rgba(93,188,191,0.3)', boxShadow: '0 8px 32px rgba(93,188,191,0.08)' }}>
                <form onSubmit={handleOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-center" style={{ color: 'var(--ink)' }}>
                      Code OTP (6 chiffres)
                    </label>
                    {/* Large input centré */}
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoFocus
                      autoComplete="one-time-code"
                      placeholder="000000"
                      className="w-full text-center font-mono-ose font-bold rounded-2xl py-4 text-3xl tracking-[0.4em]"
                      style={{
                        background: 'var(--teal-mist)',
                        border: `2px solid ${otpCode.length === 6 ? 'var(--teal)' : 'rgba(93,188,191,0.3)'}`,
                        color: 'var(--ink)',
                        letterSpacing: '0.4em'
                      }}
                    />
                    {/* Progress dots */}
                    <div className="flex justify-center gap-2 mt-3">
                      {[0,1,2,3,4,5].map(i => (
                        <div key={`list-${i}`} className="w-2 h-2 rounded-full transition-all duration-200"
                          style={{ background: i < otpCode.length ? 'var(--teal)' : 'var(--border)' }} />
                      ))}
                    </div>
                  </div>

                  {/* Avertissement tentatives */}
                  {otpAttempts > 0 && (
                    <div className="flex items-start gap-2 p-3 rounded-xl"
                      style={{ background: 'rgba(217,75,93,0.08)', border: '1px solid rgba(217,75,93,0.2)' }}>
                      <AlertTriangle size={14} style={{ color: '#D94B5D', flexShrink: 0, marginTop: '1px' }} />
                      <p className="text-xs" style={{ color: '#D94B5D' }}>
                        Code incorrect ({otpAttempts}/3 tentative{otpAttempts > 1 ? 's' : ''}).
                        {otpAttempts >= 2 && <strong> Dernière tentative avant réinitialisation.</strong>}
                      </p>
                    </div>
                  )}

                  <button type="submit"
                    disabled={loading || otpCode.length !== 6}
                    className="w-full btn-teal py-3.5 rounded-xl text-sm font-bold disabled:opacity-40 transition-all"
                  >
                    {loading ? 'Vérification…' : 'Valider le code'}
                  </button>
                </form>

                {/* Timer info */}
                <div className="text-center mt-4">
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Ce code est renouvelé toutes les 30 secondes.
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Le lien de connexion expire dans <strong>5 minutes</strong>.
                  </p>
                </div>

                {/* Retour */}
                <button onClick={goBackToCredentials}
                  className="flex items-center gap-1 text-xs mx-auto mt-4 transition-colors"
                  style={{ color: 'var(--text-muted)' }}>
                  <ArrowLeft size={13} /> Retour à la connexion
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <MobileStickyBar />
      <Footer />
    </div>
  );
}
